import { useState, useCallback, useRef, useEffect } from "react";
import { homeRooms, officeRooms, loadSizes } from "../../mocks/calculatorData";
import type { CalcType, Selections } from "../../mocks/calculatorData";
import CalcHero from "./components/CalcHero";
import RoomAccordion from "./components/RoomAccordion";
import SummaryPanel from "./components/SummaryPanel";
import CalcTips from "./components/CalcTips";
import Footer from "../home/components/Footer";
import Navbar from "../home/components/Navbar";
import QuoteModal from "../home/components/QuoteModal";

interface Toast {
  id: number;
  message: string;
}

const loadEmojis: Record<string, string> = {
  mini: "📦",
  small: "🗃️",
  medium: "🚐",
  large: "🚛",
  "large-plus": "💪",
  xlarge: "😮",
  "xlarge-plus": "😱",
  xxlarge: "🏆",
};

export default function WasteCalculatorPage() {
  const [calcType, setCalcType] = useState<CalcType>("home");
  const [selections, setSelections] = useState<Selections>({});
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);
  const prevLoadIdRef = useRef<string | null>(null);

  const rooms = calcType === "home" ? homeRooms : officeRooms;

  const pushToast = useCallback((msg: string) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message: msg }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  const handleLoadChange = useCallback((loadId: string | null) => {
    if (!loadId) return;
    const load = loadSizes.find((ls) => ls.id === loadId);
    if (load) {
      const emoji = loadEmojis[load.id] ?? "🎉";
      pushToast(`${emoji} Upgraded to ${load.label}!`);
    }
  }, [pushToast]);

  const handleUpdate = useCallback((itemId: string, qty: number) => {
    setSelections((prev) => {
      if (qty === 0) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return { ...prev, [itemId]: qty };
    });
  }, []);

  const handleTypeChange = useCallback((type: CalcType) => {
    setCalcType(type);
    setSelections({});
    prevLoadIdRef.current = null;
  }, []);

  const handleClear = useCallback(() => {
    setSelections({});
    prevLoadIdRef.current = null;
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#f9fafb" }}>
      <Navbar onBookNow={() => setQuoteOpen(true)} />
      <QuoteModal isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} />

      <CalcHero calcType={calcType} onTypeChange={handleTypeChange} />

      <main className="max-w-screen-xl mx-auto px-6 lg:px-10 py-12">
        {/* Intro strip */}
        <div
          className="rounded-3xl px-6 py-5 mb-8 flex flex-wrap items-center gap-4 thick-border"
          style={{ background: "#fff" }}
        >
          <div
            className="w-12 h-12 flex items-center justify-center rounded-2xl thick-border flex-shrink-0"
            style={{ background: "#6CC412" }}
          >
            <i
              className={`${
                calcType === "home" ? "ri-home-heart-line" : "ri-building-line"
              } text-cl-dark text-xl`}
            ></i>
          </div>
          <div className="flex-1">
            <p className="font-black text-cl-dark text-base leading-tight">
              {calcType === "home"
                ? "Open each room and tap the items you need cleared"
                : "Open each area and tap the items you need cleared"}
            </p>
            <p className="text-cl-dark/40 text-xs font-semibold mt-0.5">
              The van on the right fills up in real time — your price updates instantly ✨
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {["📦", "🛋️", "🧊"].map((e) => (
              <span
                key={e}
                className="w-9 h-9 flex items-center justify-center rounded-xl thick-border text-lg"
                style={{ background: "#f8f8f8" }}
              >
                {e}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Left: Room accordion */}
          <div className="flex-1 min-w-0">
            <RoomAccordion
              rooms={rooms}
              selections={selections}
              onUpdate={handleUpdate}
            />
            <CalcTips calcType={calcType} />
          </div>

          {/* Right: Summary panel — sticky on desktop */}
          <div className="w-full lg:w-[380px] flex-shrink-0 lg:sticky lg:top-28">
            <SummaryPanel
              rooms={rooms}
              selections={selections}
              onClear={handleClear}
              onLoadChange={handleLoadChange}
            />
          </div>
        </div>
      </main>

      <Footer />

      {/* Milestone toasts */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 flex flex-col-reverse gap-3 z-50 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="anim-toast px-6 py-3.5 rounded-full font-black text-white text-sm whitespace-nowrap thick-border cartoon-shadow"
            style={{ background: "#09245b" }}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}
